<script>
	// NPM Modules
	import dayjs from 'dayjs';
	// Stores
	import BetHistoryStore from '@/Stores/User/BetHistoryStore.js';
	// Components
	import Loading from '@/Components/Loading.svelte';
	import Pagination from '@/Components/Pagination.svelte';
	import PillMultiSelect from '@/Components/PillMultiSelect.svelte';
	import Datepicker from '@/Components/Datepicker.svelte';
	// Partials
	import DesktopBetHistory from '@/Partials/User/_DesktopBetHistory.svelte';
	import Adjustment from '@/Partials/Bets/_Adjustment.svelte';
	import Cancel from '@/Partials/Bets/_Cancel.svelte';
	import ManualResult from '@/Partials/Sport/Markets/ManualResult/_ManualResult.svelte';
	import ManualResultSGM from '@/Partials/Sport/Markets/ManualResult/_ManualResultSGM.svelte';
	// Models
	import User from '@/Models/User';
	import QueryBuilder from '@/Models/Sarala/QueryBuilder';
	// Services
	import { getUrlParameter, setUrlParameter, paginationBuilder, extractMiddleNumber } from '@/Services/Helpers.js';
	import ApiService from '@/Services/ApiService';
	import { notify } from '@/Services/Notify';

	export let user;

	$: links = $BetHistoryStore && $BetHistoryStore.count() && paginationBuilder(BetHistoryStore.getPagination());
	$: filterMeta = $BetHistoryStore && BetHistoryStore.getFilterMeta();

	let filter = {};
	let pageNo = getUrlParameter('page') || 1;
	let pageSize = 25;
	let loading = getBetHistory();
	let startDate = dayjs().format('YYYY-MM-DD');
	let endDate = dayjs().format('YYYY-MM-DD');
	let showManualResult = false;
	let showManualResultSGM = false;
	let resultEventName;
	let resultMarket;
	let resultSelection;

	function changePage(e) {
		if (e) {
			pageNo = e.detail.page;
			loading = getBetHistory();
			setUrlParameter('page', pageNo);
		}
	}

	function getBetHistory() {
		return BetHistoryStore.getUserBetHistory(
			user.id,
			{
				size: pageSize,
				number: pageNo
			},
			filter
		).catch((e) => {
			console.error(e);
			notify({
				type: 'danger',
				title: 'Error!',
				message: (e.error && e.error[0]) || 'problem occurred getting users bet history, try refreshing the page',
				timeout: 5
			});
		});
	}

	function updateFilter(e) {
		if (e.detail.name === 'min_bet_amount') {
			filter[e.detail.name] = [e.detail.checked[0]];
		} else {
			filter[e.detail.name] = e.detail.checked;
		}
	}

	function applyFilter() {
		loading = getBetHistory();
	}

	function resetFilter() {
		filter = {};
		loading = getBetHistory();
	}

	function getSelectedForCriteria(type) {
		let selected = filter[type];
		return selected ? selected : [];
	}

	function manualResult(event, market, selection) {
		if (market) {
			resultMarket = market.id;
			resultEventName = event.name;
			showManualResult = true;
		} else {
			resultSelection = extractMiddleNumber(selection);
			resultEventName = event.name;
			showManualResultSGM = true;
		}
	}

	function downloadUserBets() {
		const userModel = new User();

		this.queryBuilder = new QueryBuilder();
		this.queryBuilder.where('from', dayjs(startDate).format('YYYY-MM-DD'), 'created_at');
		this.queryBuilder.where('to', dayjs(endDate).format('YYYY-MM-DD'), 'created_at');

		if (Math.abs(dayjs(startDate).diff(dayjs(endDate), 'month')) <= 6) {
			return ApiService.downloadFile(
				`${userModel.baseUrl()}/users/${user.id}/bets/export${this.queryBuilder.getQuery()}`,
				`${user.first_name} ${user.last_name} ${dayjs(startDate).format('YYYY-MM-DD')} ${dayjs(endDate).format(
					'YYYY-MM-DD'
				)}`
			);
		}

		return notify({
			type: 'danger',
			title: 'Error!',
			message: 'Date range must be within 6 months or less',
			timeout: 5
		});
	}
</script>

<Adjustment store={$BetHistoryStore} on:saved={getBetHistory} />
<Cancel store={$BetHistoryStore} on:saved={getBetHistory} />

{#if showManualResult}
	<ManualResult
		market_id={resultMarket}
		eventName={resultEventName}
		bind:open={showManualResult}
		on:resulted={getBetHistory}
	/>
{/if}
{#if showManualResultSGM}
	<ManualResultSGM
		selection_id={resultSelection}
		eventName={resultEventName}
		bind:open={showManualResultSGM}
		on:resulted={getBetHistory}
	/>
{/if}

{#await loading}
	<Loading />
{:then}
	<div class="p-2 mx-4 bg-gray-100 rounded md:flex md:flex-col">
		<div class="flex items-center px-2 bg-dark-grey text-light">
			<h2 class="flex-1 py-2 text-xl italic font-sans-condensed">Betting History</h2>
		</div>

		<div class="flex flex-col md:flex-row items-center justify-center p-2 space-y-2 md:space-y-0 md:space-x-2 border">
			<div class="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-2 w-full md:w-auto">
				<div class="flex flex-col sm:flex-row items-center space-y-1 sm:space-y-0 sm:space-x-2">
					<div class="text-center sm:text-left">
						<span class="font-semibold">Start Date:</span>
						<Datepicker
							selected={startDate}
							displayFormat="DD/MM/YYYY"
							dateChosen={(date) => {
								startDate = dayjs(date).format('YYYY-MM-DD');
							}}
						/>
					</div>
					<div class="text-center sm:text-left">
						<span class="font-semibold">End Date:</span>
						<Datepicker
							selected={endDate}
							displayFormat="DD/MM/YYYY"
							dateChosen={(date) => {
								endDate = dayjs(date).format('YYYY-MM-DD');
							}}
						/>
					</div>
				</div>
				<button
					class="focus:outline-none w-full sm:w-auto min-h-[2rem] btn btn-primary px-3 py-2 rounded text-sm font-medium whitespace-nowrap"
					on:click|preventDefault|stopPropagation={downloadUserBets}
				>
					<span class="hidden sm:inline">Export Betting History</span>
					<span class="sm:hidden">Export</span>
				</button>
			</div>
		</div>

		<div class="flex flex-wrap md:flex-row items-center justify-center space-x-1.5 w-full p-4 text-sm md:py-2">
			{#each Object.entries(filterMeta ?? {}) as criteria}
				<PillMultiSelect
					className="min-w-24"
					bgColour="bg-grey"
					name={criteria[0]}
					label={criteria[1].name}
					on:change={(e) => updateFilter(e)}
					singleSelection={criteria[0] === 'min_bet_amount'}
					options={Object.entries(criteria[1].values)}
					action={false}
					checked={getSelectedForCriteria(criteria[0])}
				/>
			{/each}
			<button on:click={applyFilter} class="h-8 btn btn-black">Apply</button>
			<button on:click={resetFilter} class="h-8 btn btn-muted">Reset</button>
		</div>

		<DesktopBetHistory bets={$BetHistoryStore} {manualResult} on:updated={getBetHistory} />
		{#if $BetHistoryStore.count()}
			<Pagination {links} on:click={changePage} className="py-4 bg-gray-100 md:sticky md:bottom-0" />
		{/if}
	</div>
{/await}
